import { EnumKnowledgeDocumentStatus } from '../enums/knowledge-document-status.enum';
import { EnumKnowledgeIngestionStage } from '../enums/knowledge-ingestion-stage.enum';

export type KnowledgeOcrMode = 'auto' | 'force' | 'off';

/* eslint-disable @typescript-eslint/naming-convention */
export interface IKnowledgeIngestionRaw {
  id: string;
  version: number;
  status: string;
  stage: string;
  ocr_mode: KnowledgeOcrMode;
  parser_name: string | null;
  page_count: number | null;
  chunk_count: number | null;
  error_code: string | null;
  retryable: boolean;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface IKnowledgeDocumentRaw {
  id: string;
  title: string;
  original_filename: string;
  detected_mime: string;
  size_bytes: number;
  content_sha256: string;
  enabled: boolean;
  status: string;
  available_for_retrieval: boolean;
  created_at: string;
  updated_at: string;
  latest_ingestion: IKnowledgeIngestionRaw | null;
}

export interface IKnowledgeDocumentListRaw {
  items: IKnowledgeDocumentRaw[];
  next_cursor: string | null;
}

export interface IKnowledgeDeleteAcceptedRaw {
  id: string;
  status: string;
  cleanup_id: string;
}

export interface IUpdateKnowledgeDocumentRequestRaw {
  title?: string;
  enabled?: boolean;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface IKnowledgeIngestion {
  id: string;
  version: number;
  status: EnumKnowledgeDocumentStatus;
  stage: EnumKnowledgeIngestionStage;
  ocrMode: KnowledgeOcrMode;
  parserName: string | null;
  pageCount: number | null;
  chunkCount: number | null;
  errorCode: string | null;
  retryable: boolean;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface IKnowledgeDocument {
  id: string;
  title: string;
  originalFilename: string;
  detectedMime: string;
  sizeBytes: number;
  contentSha256: string;
  enabled: boolean;
  status: EnumKnowledgeDocumentStatus;
  availableForRetrieval: boolean;
  createdAt: string;
  updatedAt: string;
  latestIngestion: IKnowledgeIngestion | null;
}

export interface IKnowledgeDocumentList {
  items: IKnowledgeDocument[];
  nextCursor: string | null;
}

export interface IKnowledgeDeleteAccepted {
  id: string;
  status: EnumKnowledgeDocumentStatus;
  cleanupId: string;
}

export interface IUpdateKnowledgeDocumentRequest {
  title?: string;
  enabled?: boolean;
}

export const toKnowledgeDocumentStatus = (raw: string): EnumKnowledgeDocumentStatus => {
  const known = Object.values(EnumKnowledgeDocumentStatus);
  const match = known.find(status => status === raw);
  return match ?? EnumKnowledgeDocumentStatus.Unknown;
};

export const toKnowledgeIngestionStage = (raw: string): EnumKnowledgeIngestionStage => {
  const known = Object.values(EnumKnowledgeIngestionStage);
  const match = known.find(stage => stage === raw);
  return match ?? EnumKnowledgeIngestionStage.Unknown;
};

export const toKnowledgeIngestion = (raw: IKnowledgeIngestionRaw): IKnowledgeIngestion => ({
  id: raw.id,
  version: raw.version,
  status: toKnowledgeDocumentStatus(raw.status),
  stage: toKnowledgeIngestionStage(raw.stage),
  ocrMode: raw.ocr_mode,
  parserName: raw.parser_name,
  pageCount: raw.page_count,
  chunkCount: raw.chunk_count,
  errorCode: raw.error_code,
  retryable: raw.retryable,
  errorMessage: raw.error_message,
  createdAt: raw.created_at,
  startedAt: raw.started_at,
  finishedAt: raw.finished_at
});

export const toKnowledgeDocument = (raw: IKnowledgeDocumentRaw): IKnowledgeDocument => ({
  id: raw.id,
  title: raw.title,
  originalFilename: raw.original_filename,
  detectedMime: raw.detected_mime,
  sizeBytes: raw.size_bytes,
  contentSha256: raw.content_sha256,
  enabled: raw.enabled,
  status: toKnowledgeDocumentStatus(raw.status),
  availableForRetrieval: raw.available_for_retrieval ?? false,
  createdAt: raw.created_at,
  updatedAt: raw.updated_at,
  latestIngestion: raw.latest_ingestion ? toKnowledgeIngestion(raw.latest_ingestion) : null
});

export const toKnowledgeDocumentList = (
  raw: IKnowledgeDocumentListRaw
): IKnowledgeDocumentList => ({
  items: raw.items.map(toKnowledgeDocument),
  nextCursor: raw.next_cursor
});

export const toKnowledgeDeleteAccepted = (
  raw: IKnowledgeDeleteAcceptedRaw
): IKnowledgeDeleteAccepted => ({
  id: raw.id,
  status: toKnowledgeDocumentStatus(raw.status),
  cleanupId: raw.cleanup_id
});

export const toUpdateKnowledgeDocumentRequestRaw = (
  request: IUpdateKnowledgeDocumentRequest
): IUpdateKnowledgeDocumentRequestRaw => ({
  ...(request.title !== undefined && { title: request.title }),
  ...(request.enabled !== undefined && { enabled: request.enabled })
});
